import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { linkToEventCategoryDetail } from "#src/routes/events/event-categories/[eventCategoryId]";
import { linkToEventTemplateDetail } from "#src/routes/events/event-categories/[eventCategoryId]/templates/[templateId]/details";
import { useQuery } from "@tanstack/react-query";
import {
  Accordion,
  KeyValueList,
  KeyValuePairList,
  Skeleton,
} from "@validereinc/common-components";
import {
  EventCategoryAdapter,
  EventCategoryType,
  EventSchemaAdapter,
  EventSchemaType,
  Resources,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React from "react";
import styles from "./WorkflowActionCreateEvent.module.scss";

const cx = classNames.bind(styles);

const { AccordionPanel } = Accordion;

export const WorkflowActionCreateEvent = ({
  eventSchemaId,
}: {
  eventSchemaId: string;
}) => {
  const { data: eventSchema, isLoading: isLoadingEventSchema } = useQuery({
    queryKey: [Resources.EVENT_SCHEMA, eventSchemaId],
    queryFn: () => {
      if (!eventSchemaId) {
        return;
      }

      return EventSchemaAdapter.getOne({ id: eventSchemaId }).then(
        ({ data }) => data
      );
    },
    enabled: Boolean(eventSchemaId),
    staleTime: 3 * 60 * 1000,
  });

  const { data: eventCategory, isLoading: isLoadingEventCategory } = useQuery(
    [Resources.EVENT_CATEGORY, eventSchema?.event_category_id],
    () => {
      if (!eventSchema?.event_category_id) {
        return;
      }

      return EventCategoryAdapter.getOne({
        id: eventSchema.event_category_id,
      }).then(({ data }) => data);
    },
    {
      enabled: Boolean(eventSchema?.event_category_id),
      staleTime: 3 * 60 * 1000,
    }
  );

  const isSchemaOrCategoryLoading =
    (Boolean(eventSchemaId) && isLoadingEventSchema) ||
    (Boolean(eventSchema?.event_category_id) && isLoadingEventCategory);

  return (
    <Accordion defaultActiveKeys={["event"]}>
      <AccordionPanel
        dataKey="event"
        title="Event Overview"
      >
        <Skeleton
          isLoading={isSchemaOrCategoryLoading}
          style={{ minHeight: 200 }}
        >
          {!isSchemaOrCategoryLoading && (
            <EventDetails
              eventSchema={eventSchema}
              eventCategory={eventCategory}
            />
          )}
        </Skeleton>
      </AccordionPanel>
    </Accordion>
  );
};

const EventDetails = ({
  eventSchema,
  eventCategory,
}: {
  eventSchema?: EventSchemaType;
  eventCategory?: EventCategoryType;
}) => {
  const data: KeyValuePairList = [];

  data.push({
    title: "Category",
    value: eventCategory ? (
      <RoutingLink to={linkToEventCategoryDetail(eventCategory.id)}>
        {eventCategory.name}
      </RoutingLink>
    ) : (
      "-"
    ),
  });

  data.push({
    title: "Template",
    value: eventSchema ? (
      <RoutingLink
        to={linkToEventTemplateDetail(
          eventSchema.event_category_id,
          eventSchema.id
        )}
      >
        {eventSchema.name}
      </RoutingLink>
    ) : (
      "-"
    ),
  });

  return (
    <KeyValueList
      className={cx("keyValueListContainer")}
      data={data}
    />
  );
};
